import React, { FunctionComponent } from 'react';
import { propOr, groupBy } from 'ramda';
import withStyles from 'react-jss';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';

import { ClassProp } from 'helpers/types';
import Query from 'scenes/Report/components/Query';
import { ReportParams } from 'scenes/Report/components/ReportBase';

import { GET_SUBSCRIBER_PURCHASE_PATTERNS_BY_SEGMENT } from '../SubscriberPurchasePatterns.queries';
import styles from '../SubscriberPurchasePatterns.styles';
import { renderBarChart } from '../SubscriberPurchasePatterns.helper';

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  height?: number;
};

type BarValue = {
  segmentId: string;
  totalPurchases: string;
  percentOfMerchantTotal: number;
  aov: number;
  startDate: string;
  endDate: string;
};

type PurchasesBySegments = {
  [key: string]: any;
};

type QueryResponse = {
  purchasesBySubWithMulPurchasesBySegment: BarValue[];
};

const segmentedData = (purchasesBySubWithMulPurchases: BarValue[]) =>
  groupBy(
    (subWithMulPurchases) => propOr('Total', 'segmentId', subWithMulPurchases),
    purchasesBySubWithMulPurchases,
  );

const header = (x: string | null) =>
  `Subscriber Purchase Patterns By Segment - ${
    x === null ? 'Combined' : `${x}`
  }`;

const batchKey = 'subscriberPurchasePatternsBySegment';

const renderReport = (
  params: ReportParams,
  { classes, height = 450 }: Props,
) => ({ purchasesBySubWithMulPurchasesBySegment }: QueryResponse) => {
  const purchasesBySegments: PurchasesBySegments = segmentedData(
    purchasesBySubWithMulPurchasesBySegment,
  );
  const segments = Object.keys(purchasesBySegments);
  return (
    <>
      {segments.map((segmentId, idx) =>
        renderBarChart(
          params,
          header(segmentId),
          batchKey,
          'Percent of Purchases by Segment',
          segmentId,
          classes,
          height,
          idx,
          purchasesBySegments[segmentId],
        ),
      )}
    </>
  );
};

const SubscriberPurchasePatternsBySegment: FunctionComponent<Props> = ({
  ...rest
}) => {
  const location = useLocation();
  const params = queryString.parse(location.search) as ReportParams;
  return (
    <Query
      query={GET_SUBSCRIBER_PURCHASE_PATTERNS_BY_SEGMENT}
      reportName="Subscriber Purchase Patterns"
      batchKey={batchKey}
      variables={{ params }}
      render={renderReport(params, rest as Props)}
    />
  );
};

export default withStyles(styles)(SubscriberPurchasePatternsBySegment);
